import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getSiteSettings } from "@/services/publicApi";
import { saveAdminSiteSettings } from "@/services/adminApi";
import { useAuth } from "@/context/AuthContext";
import { siteSettings as fallbackSource } from "@/mocks/siteSettings";
import type { SiteSettings } from "@/types";
import { uploadUrl } from "@/services/config";
import { applyFavicon } from "@/utils/vendorAssets";

const FALLBACK = fallbackSource as SiteSettings;

/**
 * Contact fields that should never be blank on the site. If the backend returns
 * an empty value for one of these (e.g. a column that was never seeded, or an
 * admin clearing a field by accident), we keep the built-in default so the
 * footer / contact page always shows a real email, phone and address.
 */
const NEVER_BLANK_KEYS = ["contactEmail", "contactPhone", "address", "gstin"] as const;

/** Shallow-merges a loaded/patch payload over the current settings. */
function merge(base: SiteSettings, patch?: Partial<SiteSettings> | null): SiteSettings {
  if (!patch) return base;
  const merged: SiteSettings = {
    ...base,
    ...patch,
    social: { ...base.social, ...(patch.social ?? {}) },
  };
  NEVER_BLANK_KEYS.forEach((key) => {
    const value = merged[key];
    if (typeof value === "string" && value.trim() === "") {
      merged[key] = base[key];
    }
  });
  return merged;
}

interface SiteSettingsContextValue {
  settings: SiteSettings;
  loading: boolean;
  error: string | null;
  saving: boolean;
  reload: () => void;
  save: (patch: Partial<SiteSettings>) => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue | undefined>(undefined);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [settings, setSettings] = useState<SiteSettings>(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getSiteSettings()
      .then((data) => {
        if (active) setSettings(merge(FALLBACK, data));
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Could not load site settings.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [nonce]);

  // Apply the favicon (tab icon + apple touch icon) whenever it changes so an
  // admin can swap it without a redeploy. A remote Readdy URL is served from
  // the mirrored local copy under /uploads when available (see vendorAssets).
  useEffect(() => {
    applyFavicon(uploadUrl(settings.faviconUrl));
  }, [settings.faviconUrl]);

  const save = useCallback(
    async (patch: Partial<SiteSettings>) => {
      const previous = settings;
      setSaving(true);
      setSettings(merge(settings, patch)); // optimistic update
      try {
        await saveAdminSiteSettings(patch, token);
      } catch (err) {
        setSettings(previous); // roll back on failure
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [settings, token],
  );

  const value = useMemo<SiteSettingsContextValue>(
    () => ({ settings, loading, error, saving, reload, save }),
    [settings, loading, error, saving, reload, save],
  );

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettings(): SiteSettingsContextValue {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) throw new Error("useSiteSettings must be used within a SiteSettingsProvider");
  return ctx;
}